<template>
  <div>
    <div v-if="loading" class="h-64 p-32 flex justify-center items-center">
      <vue-element-loading
        :active="loading"
        spinner="spinner"
        color="#DBDBDB"
        background-color="rgba(255, 255, 255, .5)"
      />
    </div>
    <div class="container px-6 mt-4" v-else>
      <h1 class="uppercase text-lg pl-0 md:pl-6 font-bold">
        <span class="text-pink-500">6.</span>
        {{ $t('messages.profile_tabs.steps.your_picture')}}
      </h1>
      <p class="mt-4 px-0 md:px-6 font-semibold">{{ $t('messages.step_four.description')}}</p>
      <div class="flex mt-6 flex-wrap px-0 md:px-6">
        <div class="w-full">
          <h1
            class="uppercase text-lg font-bold text-blue-500"
          >{{ $t('messages.step_four.instructions')}}</h1>
          <div class="mt-6 text-gray-700">
            <p>- {{ $t('messages.step_four.instruction_12')}}</p>
            <p>- {{ $t('messages.step_four.instruction_1')}}</p>
            <p>- {{ $t('messages.step_four.instruction_2')}}</p>
            <p>- {{ $t('messages.step_four.instruction_3')}}</p>
            <p>- {{ $t('messages.step_four.instruction_4')}}</p>
            <p>- {{ $t('messages.step_four.instruction_5')}}</p>
            <p>- {{ $t('messages.step_four.instruction_6')}}</p>
            <p>- {{ $t('messages.step_four.instruction_7')}}</p>
            <p>- {{ $t('messages.step_four.instruction_8')}}</p>
            <p>- {{ $t('messages.step_four.instruction_9')}}</p>
            <p>- {{ $t('messages.step_four.instruction_10')}}</p>
            <p>- {{ $t('messages.step_four.instruction_11')}}</p>
          </div>
          <div class="w-full py-4">
            <h1
              class="uppercase text-lg font-bold text-blue-500"
            >{{ $t('messages.step_four.profile_photo')}}</h1>
            <div class="w-full flex flex-wrap mt-4">
              <template v-for="(photo, index) in photos">
                <template v-if="photo.status === 'pending'">
                  <div :key="index" class="w-1/3 flex flex-col items-center">
                    <img class="object-cover w-48 h-48 md:h-64 md:w-64" :src="photo.url" alt />
                    <p
                      class="text-gray-600 font-semibold"
                    >{{ $t('messages.step_four.photo_approval_pending')}}</p>
                  </div>
                </template>
              </template>
            </div>
          </div>
          <div class="w-fullbg-grey-500 mt-8">
            <h1
              class="uppercase text-lg font-bold mb-4 text-blue-500"
            >{{ $t('messages.photos.upload_photos') }}</h1>
            <vue-dropzone
              class="w-full flex flex-wrap items-center justify-center bg-grey-500"
              ref="myVueDropzone"
              id="dropzone"
              :options="dropzoneOptions"
              :useCustomSlot="true"
              :duplicateCheck="true"
              @vdropzone-error="onPhotoUploadError"
            >
              <div class="dropzone-custom-content bg-transparent">
                <h3
                  v-if="!hasUploaded"
                  class="dropzone-custom-title bg-pink-500 py-4 text-white"
                >{{ $t('messages.photos.first_message') }}</h3>
                <h3
                  v-if="hasUploaded"
                  class="dropzone-custom-title bg-pink-500 py-4 text-white"
                >{{ $t('messages.photos.first_message_more') }}</h3>
                <div class="subtitle text-gray-600">{{ $t('messages.photos.second_message') }}</div>
              </div>
            </vue-dropzone>

            <h1
              class="uppercase text-lg font-bold text-blue-500 my-4"
            >{{ $t('messages.photos.my_photos') }}</h1>
            <div class="w-full flex flex-wrap">
              <template v-for="(photo, index) in approvedPhotos">
                <template v-if="photo.status === 'approved'">
                  <div
                    @click.prevent="selectPhoto(photo)"
                    :class="[ activetab === photo.id ? 'isActive' : '' ]"
                    :key="index"
                    class="w-1/4 text-center mb-8 photo cursor-pointer"
                  >
                    <img
                      class="object-cover w-48 h-48 md:h-56 lg:h-56 xl:h-56 md:w-56 lg:w-56 xl:w-56"
                      :src="photo.url"
                      alt
                    />
                  </div>
                </template>
              </template>
            </div>
            <div v-if="approvedPhotos.length > 0" class="w-full mt-4">
              <label class="flex items-center mb-3" for="main">
                <input
                  v-model="picked"
                  class="mr-4"
                  @click="changeState(1)"
                  type="radio"
                  id="main"
                  value="main"
                />
                {{ $t('messages.step_four.options.option_1') }}
              </label>
              <label class="flex items-center mb-3" @click="changeState(2)" for="all">
                <input v-model="picked" class="mr-4" type="radio" id="all" value="all" />
                {{ $t('messages.step_four.options.option_2') }}
              </label>
              <label class="flex items-center mb-3" @click="changeState(3)" for="visible_fav">
                <input
                  v-model="picked"
                  class="mr-4"
                  type="radio"
                  id="visible_fav"
                  value="visible_fav"
                />
                {{ $t('messages.step_four.options.option_3') }}
              </label>
              <label class="flex items-center mb-3" @click="changeState(4)" for="invisible">
                <input v-model="picked" class="mr-4" type="radio" id="invisible" value="invisible" />
                {{ $t('messages.step_four.options.option_4') }}
              </label>
              <label class="flex items-center mb-3" @click.prevent="changeState(5)" for="remove">
                <input v-model="picked" class="mr-4" type="radio" id="remove" value="remove" />
                {{ $t('messages.step_four.options.option_5') }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <transition name="modal">
      <div v-if="isOpenDeleteModal">
        <div
          class="overlay h-full w-full flex justify-center items-center z-50"
          @click.self="isOpenDeleteModal = false"
        >
          <div
            class="modal p-6 bg-white rounded-lg mx-auto my-0 flex flex-col justify-center items-center"
          >
            <h1
              class="uppercase text-3xl font-bold text-blue-500"
            >{{ $t("messages.delete_photo.title") }}</h1>
            <p
              class="text-center my-6 text-gray-900 font-semibold"
            >{{ this.photos.length === 1 ? $t("messages.delete_photo.onLessQuantity.description") : $t("messages.delete_photo.description") }}</p>
            <div class="w-full flex flex-col sm:flex-row justify-center items-center">
              <div>
                <a
                  href="#"
                  @click.prevent="isOpenDeleteModal = !isOpenDeleteModal"
                  class="mx-4 bg-gray-700 hover:bg-gray-500 no-underline mb-2 sm:mb-0 text-white font-semibold rounded px-3 py-2 inline-flex"
                >{{ this.photos.length === 1 ? $t("messages.delete_photo.onLessQuantity.cancel") :$t("messages.delete_photo.cancel") }}</a>
              </div>
              <div>
                <button
                  @click="deletePhoto"
                  class="mx-4 bg-pink-500 hover:bg-pink-600 no-underline text-white font-semibold rounded px-3 py-2 inline-flex"
                >{{this.photos.length === 1 ? $t("messages.delete_photo.onLessQuantity.accept") : $t("messages.delete_photo.accept") }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="isOpenMaxFileExceeded">
        <div
          class="overlay h-full w-full flex justify-center items-center z-50"
          @click.self="isOpenMaxFileExceeded = false"
        >
          <div
            class="modal p-6 bg-white rounded-lg mx-auto my-0 flex flex-col justify-center items-center"
          >
            <h1
              class="uppercase text-3xl font-bold text-blue-500"
            >{{ $t("messages.profile.photos.max_file_exceeded.title") }}</h1>
            <p
              class="text-center my-6 text-gray-900 font-semibold"
            >{{ $t("messages.profile.photos.max_file_exceeded.description") }}</p>
            <div class="w-full flex flex-col sm:flex-row justify-center items-center">
              <div>
                <a
                  href="#"
                  @click.prevent="isOpenMaxFileExceeded = !isOpenMaxFileExceeded"
                  class="mx-4 no-underline mb-2 sm:mb-0 text-white font-semibold rounded px-3 py-2 inline-flex bg-pink-500 border hover:text-pink-500 hover:bg-white hover:border-pink-500"
                >{{ $t("messages.profile.photos.max_file_exceeded.accept") }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal">
      <div v-if="isCropping">
        <div
          class="overlay h-full w-full flex justify-center items-center z-50"
          @click.self="isOpenMaxFileExceeded = false"
        >
          <div
            class="modal p-6 bg-white rounded-lg mx-auto my-0 flex flex-col justify-center items-center"
          >
            <div class="img-cropper">
              <div class="w-full flex flex-col sm:flex-row justify-center items-center">
                <div>
                  <a
                    href="#"
                    @click.prevent="cropImage"
                    class="mx-4 no-underline mb-2 sm:mb-0 text-white font-semibold rounded px-3 py-2 inline-flex bg-pink-500 border hover:text-pink-500 hover:bg-white hover:border-pink-500"
                  >{{ $t("messages.profile.photos.max_file_exceeded.accept") }}</a>
                </div>
              </div>
            <vue-cropper
              ref="cropper"
              :src="toCropImage"
              alt="Source Image"
              :aspectRatio="1 / 1"
              :dragMode="'move'"
              :cropBoxMovable="true"
              :cropBoxResizable="true"
              :minCropBoxWidth="100"
              :minCropBoxHeight="100"
            >
            </vue-cropper>
            <div class="w-full flex flex-col sm:flex-row justify-center items-center">
              <div>
                <a
                  href="#"
                  @click.prevent="cropImage"
                  class="mx-4 no-underline mb-2 sm:mb-0 text-white font-semibold rounded px-3 py-2 inline-flex bg-pink-500 border hover:text-pink-500 hover:bg-white hover:border-pink-500"
                >{{ $t("messages.profile.photos.max_file_exceeded.accept") }}</a>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { v4 as uuidv1 } from 'uuid';

import firebase from "firebase/app";
import vue2Dropzone from "vue2-dropzone";
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import "firebase/storage";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import _ from "lodash";
import Compressor from 'compressorjs';

import bus from "@/bus";

import { mapActions, mapState } from "vuex";
export default {
  name: "StepSix",
  props: ["clickedNext", "currentStep", "loading"],
  mixins: [validationMixin],
  components: {
    vueDropzone: vue2Dropzone,
    VueCropper
  },
  data: function() {
    return {
      activetab: 1,
      selectedPicture: null,
      principalPic: "no",
      foto: [],
      picked: false,
      isOpenDeleteModal: false,
      isOpenMaxFileExceeded: false,
      isCropping: false,
      toCropImage: null,
      cropImg: null,
      approvedPhotos: [],
      form: {
        name: ""
      },
      data: {
        id: "",
        pictures: []
      },
      dropzoneOptions: {
        url: '/api/web/temp-post',
        withCredentials: false,
        thumbnailWidth: 150,
        maxFilesize: 12,
        addRemoveLinks: true,
        maxFiles: 10,
        timeout: 3000000,
        uploadMultiple: false,
        transformFile: async (file, done) => {
          if (file.dropped) {
              done(file);
              return;
            }

          this.isCropping = true;
          this.toCropImage = URL.createObjectURL(file);

          bus.$on("was-cropped", async () => {
             if (file.dropped) {
                done(file);
              }

              if (!file.dropped) {
                this.$refs.myVueDropzone.removeFile(file);
                const format = file.type;

                await this.cropImg.toBlob(async (blob) => {
                  await new Compressor(blob, {
                    quality: 0.6,
                    maxWidth: 600,
                    maxHeight: 600,
                    success: (result) => {
                      result.dropped = true;
                      result.lastModifiedDate = 1581465610000;
                      result.name = `newUserImage.${format[1]}`;

                      this.$refs.myVueDropzone.addFile(result);
                      done(result);
                      this.isCropping = false;
                    },
                    error(err) {
                      console.log(err.message);
                    },
                  });
              });
              }
          });
        }
      },
      hasUploaded: false
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("profiles", ["photos"])
  },
  created() {
    this.id = this.user.profile.id;
  },
  async mounted() {
    await this.showPictures();

    this.approvedPhotos = this.photos.filter(photo => photo.status === 'approved');
  },
  methods: {
    ...mapActions("auth", ["fetchUser", "savePhotos"]),
    ...mapActions("profiles", [
      "showImages",
      "selectProfile",
      "changeVisible",
      "changeVisibleFav",
      "uploadImageLimited",
      "changeInvisible",
      "deletePicture"
    ]),
    cropImage() {
      this.cropImg = this.$refs.cropper.getCroppedCanvas();
      this.isCropping = false;

      bus.$emit("was-cropped");
    },
    onPhotoUploadError(file) {
      this.$refs.myVueDropzone.removeFile(file);
    },
    async showPictures() {
      await this.showImages(this.user.id);
    },
    async selectPrincipal(item) {
      await this.selectProfile(item);
      await this.showPictures();
      await this.fetchUser(true);
    },
    async selectVisible(item) {
      await this.changeVisible(item);
      await this.showPictures();
      await this.fetchUser(true);
    },
    async selectVisibleFav(item) {
      await this.changeVisibleFav(item);
      await this.showPictures();
      await this.fetchUser(true);
    },
    async selectInvisible(item) {
      await this.changeInvisible(item);
      await this.showPictures();
      await this.fetchUser(true);
    },
    async deletePhoto() {

      let Title = `<p class='uppercase text-3xl font-bold text-blue-500'> ${this.$t('messages.delete_photo.title')} </p>`;
      let Text = '';
      let Accept = '';
      let Cancel = '';

      if (this.user.subscription_status !== 'active' &&  this.photos.length === 1) {
        Text = this.$t('messages.delete_photo.onLessQuantity.description');
        Accept = this.$t('messages.delete_photo.onLessQuantity.accept');
        Cancel = this.$t('messages.delete_photo.onLessQuantity.cancel');
      } else {
        Text = this.$t('messages.delete_photo.description');
        Accept = this.$t('messages.delete_photo.accept');
        Cancel = this.$t('messages.delete_photo.cancel');
      }

      this.$swal
          .fire({
            title: Title,
            text: Text,
            cancelButtonText: Cancel,
            confirmButtonText: Accept,
            showCancelButton: true,

          }).then( async result => {
            if (result.value) {
                await this.deletePicture(this.selectedPicture);
               _.remove(this.approvedPhotos,  x => {
                 return x == this.selectedPicture;
               })
                await this.showPictures();
                await this.fetchUser(true);
                this.isOpenDeleteModal = false;
                this.picked = false;
            }
          });
    },
    async changeState(item) {
      switch (item) {
        case 1:
          this.selectPrincipal(this.selectedPicture);
          break;
        case 2:
          this.selectVisible(this.selectedPicture);
          break;
        case 3:
          this.selectVisibleFav(this.selectedPicture);
          break;
        case 4:
          this.selectInvisible(this.selectedPicture);
          break;
        case 5:
          this.deletePhoto();
          // this.$dialog
          //   .confirm("¿Estas seguro que quieres eliminar esta imagen?")
          //   .then(async dialog => {
          //     await this.deletePicture(this.selectedPicture);
          //     await this.showPictures();
          //   })
          //   .catch(function(e) {
          //     return e;
          //   });
          break;
        default:
          break;
      }
    },
    selectPhoto(photo) {
      this.selectedPicture = photo;
      this.activetab = photo.id;
      if (document.getElementById("main").disabled == true) {
        document.getElementById("main").disabled = false;
        document.getElementById("all").disabled = false;
        document.getElementById("visible_fav").disabled = false;
        document.getElementById("invisible").disabled = false;
      }

      if (photo.cover == true) {
        document.getElementById("main").click();
      } else if (photo.is_visible == true) {
        document.getElementById("all").click();
      } else if (photo.is_visible_favorite == true) {
        document.getElementById("visible_fav").click();
      } else if (photo.is_visible === false) {
        document.getElementById("invisible").click();
      }
    },
    async createPictures() {
      let images = this.$refs.myVueDropzone.getAcceptedFiles();

      const total = images.length + this.photos.length;

      if (total > 10) {
        this.isOpenMaxFileExceeded = true;
        return;
      }

      this.foto = [];
      for (let image in images) {
        await this.uploadImage(images[image]);
      }
      this.data.id = this.user.id;
      this.data.pictures = this.foto;
      await this.savePhotos(this.data);
      await this.showPictures();

      this.hasUploaded = true;
    },
    async uploadImage(image) {
      if ((this.user.subscription_status ? this.user.subscription_status !== 'active' : true)) {
        let limited = await this.uploadImageLimited(this.user.id);
        if (limited){
          this.$swal
          .fire({
            customClass: {
              cancelButton: 'swalBntPadding'
            },
            html:`<b><p style = ' color: #1c4c91;'> ${ this.$t('picture_limit.message')}</p></b> <br>`,
            width: 720,
            confirmButtonText: this.$t('picture_limit.subscribe'),
            cancelButtonText:  this.$t('picture_limit.cancel'),
            cancelButtonColor:  '#949697',
            confirmButtonColor:'#1C4C91',
            showCancelButton: true,
            reverseButtons: true,
            background: 'center #EFF3F2 url(https://jwperfectmatch.com/storage/image/banner-profile-subscribe.webp) no-repeat'
          }).then(result =>{
            console.log(result.value);
              if (result.value) {
                this.$router.push('../subscriptions');
              }
            });
          return;
        }
      }
      let name = image.name;
      let ext = name.split(".");
      ext = name[1];
      let lafoto = "";
      let logoName = `${uuidv1()}.${ext}`;
      let logo = await firebase
        .storage()
        .ref(`profile/${logoName}`)
        .put(image);
      lafoto = await logo.ref.getDownloadURL();
      this.foto.push({ url: lafoto });
    }
  }
};
</script>
<style>
  .swalBntPadding {
  margin-right: 70px;
}
</style>
<style scoped>
#dropzone >>> .dz-message {
  display: flex !important;
  justify-content: center !important;
  width: 100% !important;
}

.img-cropper {
  max-height: 600px;
  max-width: 500px;
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.isActive {
  opacity: 1 !important;
}
.photo {
  opacity: 0.4;
}
#dropzone {
  background-color: #f6f6f6 !important;
  .dz-message {
    padding: 10px;
    background-color: transparent;
    color: white;
    font-weight: 600;
  }
}
input[type="radio"] {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border: 1px solid darkgray;
  border-radius: 50%;
  outline: none;
  box-shadow: 0 0 5px 0px gray inset;
}
input[type="radio"]:hover {
  box-shadow: 0 0 5px 0px orange inset;
}
input[type="radio"]:before {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  /*margin: 20% auto;    */
  border-radius: 50%;
}
input[type="radio"]:checked:before {
  background: #0669f9;
}
.ios8-switch:checked + label {
  font-weight: bold;
}
input[type="checkbox"].ios8-switch {
  position: absolute;
  margin: 10px 0 0 20px;
  + label {
    position: relative;
    padding: 7px 0 0 60px;
    line-height: 2.3em;
    &:before {
      content: "";
      position: absolute;
      display: block;
      left: 0;
      top: 0;
      width: 50px;
      height: 30px;
      border-radius: 20px;
      background: #fff;
      border: 1px solid #d9d9d9;
      -webkit-transition: all 0.3s;
      transition: all 0.3s;
    }
    &:after {
      content: "";
      position: absolute;
      display: block;
      left: 0px;
      top: 0px;
      width: 30px;
      height: 30px;
      border-radius: 20px;
      background: #fff;
      border: 1px solid #d9d9d9;
      -webkit-transition: all 0.3s;
      transition: all 0.3s;
    }
  }
}
input[type="checkbox"].ios8-switch + label:hover:after {
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
}
input[type="checkbox"].ios8-switch:checked + label:after {
  margin-left: 20px;
}
input[type="checkbox"].ios8-switch:checked + label:before {
  background: #0669f9;
}
</style>
